/**
 * ASCII Art Generator - CRT 扫描线效果
 *
 * 完整CRT效果：扫描线 + 屏幕弯曲 + 边缘色散 + 晕影
 */

import type { CRTConfig } from './types'
import { clamp } from './helpers'

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用完整 CRT 效果到 Canvas
 * @param canvas 目标画布
 * @param config CRT 配置
 */
export function applyCRT(
  canvas: HTMLCanvasElement,
  config: CRTConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { scanlineStrength, scanlineSpacing, curvature, edgeRgbShift, crtVignette } = config

  // ============================================
  // 第一部分：屏幕弯曲变形（最复杂）
  // ============================================
  if (curvature > 0) {
    applyCurvature(ctx, canvas, width, height, curvature)
  }

  // ============================================
  // 第二部分：扫描线效果
  // ============================================
  if (scanlineStrength > 0) {
    applyScanlines(ctx, width, height, scanlineStrength, scanlineSpacing)
  }

  // ============================================
  // 第三部分：边缘 RGB 色散
  // ============================================
  if (edgeRgbShift > 0) {
    applyEdgeRgbShift(ctx, canvas, width, height, edgeRgbShift)
  }

  // ============================================
  // 第四部分：CRT 晕影
  // ============================================
  if (crtVignette > 0) {
    applyCRTVignette(ctx, width, height, crtVignette)
  }
}

// ============================================
// 扫描线实现
// ============================================

/**
 * 应用扫描线效果
 */
function applyScanlines(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strength: number,
  spacing: number
): void {
  ctx.globalCompositeOperation = 'multiply'

  // 提高强度系数使扫描线更明显
  const lineAlpha = strength * 0.8
  ctx.fillStyle = `rgba(0, 0, 0, ${lineAlpha})`

  // 绘制水平扫描线
  for (let y = 0; y < height; y += spacing) {
    ctx.fillRect(0, y, width, 1)
  }

  ctx.globalCompositeOperation = 'source-over'
}

// ============================================
// 屏幕弯曲实现
// ============================================

/**
 * 应用屏幕弯曲变形
 * 使用像素重映射模拟CRT显示器凸面
 */
function applyCurvature(
  ctx: CanvasRenderingContext2D,
  _canvas: HTMLCanvasElement,
  width: number,
  height: number,
  curvature: number
): void {
  // 获取原始图像数据
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  // 创建变形后的数据
  const warpedData = new Uint8ClampedArray(data.length)
  const centerX = width / 2
  const centerY = height / 2

  // 弯曲系数
  const k = curvature * 0.5

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // 计算距离中心的相对位置
      const nx = (x - centerX) / centerX
      const ny = (y - centerY) / centerY

      // 应用弯曲变形公式
      // CRT 弯曲：边缘像素向外扩展
      const rx = nx * (1 + k * ny * ny)
      const ry = ny * (1 + k * nx * nx)

      // 转换回像素坐标
      const srcX = Math.round(centerX + rx * centerX)
      const srcY = Math.round(centerY + ry * centerY)

      // 获取源像素（超出范围则用边缘）
      const clampedX = clamp(srcX, 0, width - 1)
      const clampedY = clamp(srcY, 0, height - 1)

      const srcI = (clampedY * width + clampedX) * 4
      const destI = (y * width + x) * 4

      // 复制像素
      warpedData[destI] = data[srcI]
      warpedData[destI + 1] = data[srcI + 1]
      warpedData[destI + 2] = data[srcI + 2]
      warpedData[destI + 3] = data[srcI + 3]
    }
  }

  // 写回变形数据
  for (let i = 0; i < data.length; i++) {
    data[i] = warpedData[i]
  }

  ctx.putImageData(imageData, 0, 0)
}

// ============================================
// 边缘 RGB 色散实现
// ============================================

/**
 * 应用边缘 RGB 色散
 * 色散主要集中在画面边缘
 */
function applyEdgeRgbShift(
  ctx: CanvasRenderingContext2D,
  _canvas: HTMLCanvasElement,
  width: number,
  height: number,
  edgeRgbShift: number
): void {
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data
  const originalData = new Uint8ClampedArray(data)

  const centerX = width / 2
  const centerY = height / 2
  const maxDist = Math.max(centerX, centerY)

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4

      // 计算距离中心的距离
      const dx = x - centerX
      const dy = y - centerY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const distRatio = dist / maxDist

      // 边缘色散强度（中心为0，边缘最大）
      const shift = Math.round(edgeRgbShift * distRatio)

      // 红通道向右偏移
      const redX = clamp(x + shift, 0, width - 1)
      const redI = (y * width + redX) * 4
      data[i] = originalData[redI]

      // 蓝通道向左偏移
      const blueX = clamp(x - shift, 0, width - 1)
      const blueI = (y * width + blueX) * 4
      data[i + 2] = originalData[blueI + 2]
    }
  }

  ctx.putImageData(imageData, 0, 0)
}

// ============================================
// CRT 晕影实现
// ============================================

/**
 * 应用 CRT 晕影
 * 比普通晕影更强调边缘暗化
 */
function applyCRTVignette(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strength: number
): void {
  const centerX = width / 2
  const centerY = height / 2
  const radius = Math.max(width, height) * 0.6

  // 创建径向渐变 - 提高强度使效果更明显
  const gradient = ctx.createRadialGradient(
    centerX, centerY, radius * 0.5,
    centerX, centerY, radius
  )

  const darkAlpha = strength * 1.2
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
  gradient.addColorStop(0.5, `rgba(0, 0, 0, ${darkAlpha * 0.2})`)
  gradient.addColorStop(0.75, `rgba(0, 0, 0, ${darkAlpha * 0.5})`)
  gradient.addColorStop(1, `rgba(0, 0, 0, ${darkAlpha})`)

  ctx.globalCompositeOperation = 'multiply'
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, width, height)
  ctx.globalCompositeOperation = 'source-over'
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * CRT 效果类
 */
export class CRTEffect {
  private config: CRTConfig
  private time: number = 0

  constructor(config: CRTConfig) {
    this.config = config
  }

  /**
   * 更新时间（CRT效果主要静态，但可用于动态变化）
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 更新配置
   */
  updateConfig(config: CRTConfig): void {
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    applyCRT(canvas, this.config)
  }

  /**
   * 重置
   */
  reset(): void {
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建 CRT 配置
 */
export function createCRTConfigFromSettings(
  scanlineStrength: number,
  scanlineSpacing: number,
  curvature: number,
  edgeRgbShift: number,
  crtVignette: number
): CRTConfig {
  return {
    scanlineStrength: clamp(scanlineStrength, 0, 1),
    scanlineSpacing: clamp(Math.round(scanlineSpacing), 2, 4),
    curvature: clamp(curvature, 0, 0.5),
    edgeRgbShift: clamp(Math.round(edgeRgbShift), 0, 10),
    crtVignette: clamp(crtVignette, 0, 1),
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认 CRT 配置 - 参数已调整使效果更明显 */
export const DEFAULT_CRT_CONFIG: CRTConfig = {
  scanlineStrength: 0.5,
  scanlineSpacing: 2,
  curvature: 0.25,
  edgeRgbShift: 4,
  crtVignette: 0.4,
  time: 0
}